// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.VirtualFormFrontSideStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

const DEFAULT_FIRST_VIEW_HEIGHT_RATIO = 10;
const DEFAULT_VIEW_HEIGHT_RATIO = 1;
const HEIGHT_BUFFER = 50;

// $FlowFixMe
import { slice, includes, findIndex, drop } from 'lodash/fp';

class VirtualFormFrontSideStore {
  first: boolean = true;
  rendered: boolean = false;
  items: Array<any> = [];
  viewHeight: number = 0;
  heightForRender: number = 0; // When the cursor is scrolled to this position, it will render for new items
  heightTotal: number = 0;
  heightTotalOnAViewScreen = 0;
  startIndex: number = 0;
  renderedIds: Array<any> = [];
  virtualSections: Array<any> = [];

  constructor(viewHeight: any) {
    this.viewHeight = viewHeight;
    this.heightForRender = viewHeight;
  }

  /**
   * get container height
   * @returns {Number} container height
   */
  getContainerHeight() {
    return this.first ? this.viewHeight * DEFAULT_FIRST_VIEW_HEIGHT_RATIO : this.viewHeight * DEFAULT_VIEW_HEIGHT_RATIO;
  }

  /**
   * get items on screen
   * @returns {Array} items
   */
  getItemsOnViewScreen() {
    return this.first ? this.items : slice(this.startIndex, this.items.length, this.items);
  }

  /**
   * has vacancy
   * @param {Number} height
   * @param {String} reportPartDomId
   * @param {Number} index
   * @returns {Boolean} has vacancy
   */
  hasVacancy(height: number, reportPartDomId: string) {
    this.heightTotal += height;
    this.heightTotalOnAViewScreen += height;

    if (this.heightTotalOnAViewScreen > this.getContainerHeight()) {
      this.heightTotalOnAViewScreen = 0;
      this.startIndex = findIndex(['reportPartDomId', reportPartDomId], this.items) + 1;
      this.first = false;
      return false;
    }

    return true;
  }

  /**
   * check cursor position and create virtual section position
   * @param {Number} scrollTop
   * @returns {Boolean}
   */
  hasNeedToCreateVirtualSectionPosition(scrollTop: number) {
    if (this.heightForRender - HEIGHT_BUFFER < scrollTop) {
      this.heightForRender += this.viewHeight;
      return true;
    }
    return false;
  }

  /**
   * check whether item is rendered or not.
   * @returns {Boolean}
   */
  checkRenderedItem(reportPartDomId: string) {
    return includes(reportPartDomId, this.renderedIds);
  }

  /**
   * update items
   * @param {Array} data
   */
  updateItems(data: any) {
    this.items = data;
  }

  /**
   * has virtual section
   * @param {Number} scrollTop
   * @returns {Boolean}
   */
  hasVirtualSection(scrollTop: number) {
    if (this.hasNeedToCreateVirtualSectionPosition(scrollTop)) {
      return createVirtualSection.apply(this);
    }

    return false;

    function createVirtualSection() {
      const index = Math.floor(scrollTop / this.viewHeight);
      if (!includes(index, this.virtualSections)) {
        this.virtualSections.push(index);
        return true;
      }
      return false;
    }
  }

  /**
   * update virtual sections
   */
  updateVirtualSections() {
    this.virtualSections = drop(1, this.virtualSections);
  }

  /**
   * stop rendering
   */
  stop() {
    this.heightForRender = this.heightTotal;
  }

  /**
   * update items which is rendered
   */
  updateRenderedIds(reportPartDomId: string) {
    this.renderedIds.push(reportPartDomId);
  }

  /**
   * Clean up all render items.
   */
  cleanUp() {
    this.renderedIds.splice(0, this.renderedIds.length);
    this.viewHeight = 0;
    this.heightForRender = 0; // When the cursor is scrolled to this position, it will render for new items
    this.heightTotal = 0;
    this.heightTotalOnAViewScreen = 0;
    this.first = true;
    this.rendered = false;
    this.startIndex = 0;
    this.virtualSections.splice(0, this.virtualSections.length);
  }
}

export default VirtualFormFrontSideStore;



// WEBPACK FOOTER //
// ./ui/logic/Izenda.VirtualFormFrontSideStore.js